import {createSlice} from '@reduxjs/toolkit';

/**
 * @fileoverview Redux `chat` slice: Insights + ChatPanel shared state (`chatHistory`, panel layout,
 * `constantOverrides`). Actions: `addMessage`, `updateLastMessage` (SSE streaming), `clearChatHistory`,
 * `setConstantOverride`, `setPanelFrame`, `toggleChat`, etc.
 */

const MIN_PANEL_HEIGHT = 200;

/**
 * @param {Object} state
 */
function persistPanelLayout(state) {
  try {
    localStorage.setItem('chat_panel_width', String(state.panelWidth));
    localStorage.setItem('chat_panel_left', String(state.panelLeft));
    localStorage.setItem('chat_panel_top', String(state.panelTop));
    localStorage.setItem('chat_panel_height', String(state.panelHeight));
  } catch (e) {
    console.error('Failed to save chat panel layout:', e);
  }
}

/**
 * @return {Object} Default chat slice state.
 */
const loadInitialState = () => {
  try {
    const savedWidth = localStorage.getItem('chat_panel_width');
    const savedDim = localStorage.getItem('chat_overlay_dim');
    const savedLeft = localStorage.getItem('chat_panel_left');
    const savedTop = localStorage.getItem('chat_panel_top');
    const savedHeight = localStorage.getItem('chat_panel_height');
    const savedInsetTop = localStorage.getItem('chat_panel_inset_top');
    const savedInsetBottom = localStorage.getItem('chat_panel_inset_bottom');

    const winW = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const winH = typeof window !== 'undefined' ? window.innerHeight : 800;
    const panelWidth = savedWidth ? parseInt(savedWidth, 10) : 400;

    let panelLeft;
    let panelTop;
    let panelHeight;

    if (savedLeft != null && savedTop != null && savedHeight != null) {
      panelLeft = parseInt(savedLeft, 10);
      panelTop = parseInt(savedTop, 10);
      panelHeight = parseInt(savedHeight, 10);
    } else {
      const insetT = savedInsetTop ? parseInt(savedInsetTop, 10) : 0;
      const insetB = savedInsetBottom ? parseInt(savedInsetBottom, 10) : 0;
      panelLeft = winW - panelWidth;
      panelTop = insetT;
      panelHeight = Math.max(MIN_PANEL_HEIGHT, winH - insetT - insetB);
    }

    return {
      isOpen: false,
      chatHistory: [],
      panelWidth,
      panelLeft,
      panelTop,
      panelHeight,
      overlayDimEnabled: savedDim !== '0',
      threads: [],
      activeThreadId: null,
      historyPanelOpen: false,
      threadsLoading: false,
      threadsError: null,
      constantOverrides: {},
      textDefinitionMode: false,
    };
  } catch (e) {
    return {
      isOpen: false,
      chatHistory: [],
      panelWidth: 400,
      panelLeft: 800,
      panelTop: 0,
      panelHeight: 800,
      overlayDimEnabled: true,
      threads: [],
      activeThreadId: null,
      historyPanelOpen: false,
      threadsLoading: false,
      threadsError: null,
      constantOverrides: {},
      textDefinitionMode: false,
    };
  }
};

const chatSlice = createSlice({
  name: 'chat',
  initialState: loadInitialState(),
  reducers: {
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
    },
    setChatOpen: (state, action) => {
      state.isOpen = action.payload;
    },
    addMessage: (state, action) => {
      // If threadId is provided, only add if it matches active thread
      const {threadId, ...message} = action.payload;
      if (threadId && state.activeThreadId && threadId !== state.activeThreadId) {
        // Ignore messages for non-active threads (they're saved in Redis anyway)
        return;
      }
      state.chatHistory.push(message);
      state.textDefinitionMode = false;
    },
    setTextDefinitionMode: (state, action) => {
      state.textDefinitionMode = action.payload;
    },
    triggerClearTextInput: (state) => {
      state.clearTextTrigger = (state.clearTextTrigger || 0) + 1;
    },
    updateLastMessage: (state, action) => {
      // If threadId is provided, only update if it matches active thread
      const {threadId, ...updates} = action.payload;
      if (threadId && state.activeThreadId && threadId !== state.activeThreadId) {
        // Ignore updates for non-active threads (they're saved in Redis anyway)
        return;
      }
      if (state.chatHistory.length > 0) {
        const lastMessage = state.chatHistory[state.chatHistory.length - 1];
        Object.assign(lastMessage, {...updates, statusUpdate: updates.statusUpdate || false});
      }
    },
    removeLastMessageIfEmpty: (state, action) => {
      // Drop a trailing assistant placeholder if Stop was hit before any content streamed in.
      const {threadId} = action.payload || {};
      if (threadId && state.activeThreadId && threadId !== state.activeThreadId) return;
      const last = state.chatHistory[state.chatHistory.length - 1];
      if (last && last.role === 'assistant' && !last.content && !last.html && !last.queryResults) {
        state.chatHistory.pop();
      }
    },
    setActiveThreadId: (state, action) => {
      state.activeThreadId = action.payload;
    },
    clearChatHistory: (state) => {
      state.chatHistory = [];
      state.activeThreadId = null;
      state.constantOverrides = {};
      try {
        sessionStorage.removeItem('chat_constant_overrides');
      } catch (e) {
        console.error('Failed to clear chat history:', e);
      }
    },
    setThreads: (state, action) => {
      state.threads = Array.isArray(action.payload) ? action.payload : [];
    },
    setThreadsLoading: (state, action) => {
      state.threadsLoading = action.payload;
    },
    setThreadsError: (state, action) => {
      state.threadsError = action.payload;
    },
    startNewChat: (state) => {
      state.chatHistory = [];
      state.activeThreadId = null;
    },
    loadThread: (state, action) => {
      const {threadId, chatHistory} = action.payload;

      state.chatHistory = Array.isArray(chatHistory) ? [...chatHistory] : [];
      state.activeThreadId = threadId;
    },
    removeThreadFromState: (state, action) => {
      const id = action.payload;
      state.threads = state.threads.filter((t) => {
        return t.id !== id;
      });
      if (state.activeThreadId === id) {
        state.chatHistory = [];
        state.activeThreadId = null;
      }
    },
    setHistoryPanelOpen: (state, action) => {
      state.historyPanelOpen = action.payload;
    },
    setConstantOverride: (state, action) => {
      // action.payload = { key: 'costPerIdleHour', value: 50 }
      const {key, value} = action.payload;
      state.constantOverrides[key] = value;
      try {
        sessionStorage.setItem('chat_constant_overrides', JSON.stringify(state.constantOverrides));
      } catch (e) {
        console.error('Failed to save constant override:', e);
      }
    },
    clearConstantOverride: (state, action) => {
      // action.payload = 'costPerIdleHour' (key to clear)
      const key = action.payload;
      delete state.constantOverrides[key];
      try {
        sessionStorage.setItem('chat_constant_overrides', JSON.stringify(state.constantOverrides));
      } catch (e) {
        console.error('Failed to save constant overrides:', e);
      }
    },
    clearAllConstantOverrides: (state) => {
      state.constantOverrides = {};
      try {
        sessionStorage.removeItem('chat_constant_overrides');
      } catch (e) {
        console.error('Failed to clear constant overrides:', e);
      }
    },
    /**
     * Partial layout (already clamped by ChatPanel for viewport + sidebar).
     * @param {Object} state Chat slice state (Immer draft).
     * @param {Object} action Redux action; `payload` may include `left`, `top`, `width`, `height`.
     */
    setPanelFrame: (state, action) => {
      const p = action.payload;
      if (typeof p.width === 'number') {
        state.panelWidth = p.width;
      }
      if (typeof p.left === 'number') {
        state.panelLeft = p.left;
      }
      if (typeof p.top === 'number') {
        state.panelTop = p.top;
      }
      if (typeof p.height === 'number') {
        state.panelHeight = p.height;
      }
      persistPanelLayout(state);
    },
    setChatOverlayDimEnabled: (state, action) => {
      state.overlayDimEnabled = action.payload;
      try {
        localStorage.setItem('chat_overlay_dim', action.payload ? '1' : '0');
      } catch (e) {
        console.error('Failed to save overlay dim preference:', e);
      }
    },
    setMessageFeedback: (state, action) => {
      const {index, feedback} = action.payload;
      if (index >= 0 && index < state.chatHistory.length) {
        state.chatHistory[index].feedback = feedback;
      }
    },
  },
});

export const {
  toggleChat,
  setChatOpen,
  addMessage,
  updateLastMessage,
  removeLastMessageIfEmpty,
  setActiveThreadId,
  clearChatHistory,
  setPanelFrame,
  setConstantOverride,
  clearConstantOverride,
  clearAllConstantOverrides,
  setThreads,
  setThreadsLoading,
  setThreadsError,
  startNewChat,
  loadThread,
  removeThreadFromState,
  setHistoryPanelOpen,
  setChatOverlayDimEnabled,
  setMessageFeedback,
  setTextDefinitionMode,
  triggerClearTextInput,
} = chatSlice.actions;

export default chatSlice.reducer;
